import * as React from "react";
import { Link } from "react-router-dom";
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle, Award } from "lucide-react";
import { cn } from "@/lib/utils";

export type ToastType = "success" | "error" | "info" | "warning" | "achievement";

export interface AchievementData {
  title: string;
  description?: string;
  xp?: number;
  imageUrl?: string;
}

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  /** Only when type is "achievement" */
  achievement?: AchievementData;
}

interface ToastProps {
  toast: Toast;
  onClose: (id: string) => void;
}

const ToastComponent = React.forwardRef<HTMLDivElement, ToastProps>(
  ({ toast, onClose }, ref) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const [isRemoving, setIsRemoving] = React.useState(false);

    React.useEffect(() => {
      // Trigger animation
      setTimeout(() => setIsVisible(true), 10);

      // Auto-dismiss
      const duration = toast.duration ?? 5000;
      if (duration > 0) {
        const timer = setTimeout(() => {
          setIsRemoving(true);
          setTimeout(() => {
            onClose(toast.id);
          }, 300);
        }, duration);

        return () => clearTimeout(timer);
      }
    }, [toast.duration, toast.id, onClose]);

    const handleClose = () => {
      setIsRemoving(true);
      setTimeout(() => {
        onClose(toast.id);
      }, 300); // Match animation duration
    };

    const icons: Record<ToastType, React.ComponentType<{ className?: string }>> = {
      success: CheckCircle2,
      error: AlertCircle,
      info: Info,
      warning: AlertTriangle,
      achievement: Award,
    };

    const styles: Record<ToastType, string> = {
      success: "bg-green-50 border-green-200 text-green-900 dark:bg-green-950 dark:border-green-800 dark:text-green-100",
      error: "bg-red-50 border-red-200 text-red-900 dark:bg-red-950 dark:border-red-800 dark:text-red-100",
      info: "bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-100",
      warning: "bg-yellow-50 border-yellow-200 text-yellow-900 dark:bg-yellow-950 dark:border-yellow-800 dark:text-yellow-100",
      achievement: "bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-100",
    };

    const iconColors: Record<ToastType, string> = {
      success: "text-green-600 dark:text-green-400",
      error: "text-red-600 dark:text-red-400",
      info: "text-blue-600 dark:text-blue-400",
      warning: "text-yellow-600 dark:text-yellow-400",
      achievement: "text-amber-600 dark:text-amber-400",
    };

    const Icon = icons[toast.type];

    // Achievement toast: clickable card linking to achievements page
    if (toast.type === "achievement" && toast.achievement) {
      const a = toast.achievement;
      return (
        <div ref={ref}>
        <Link
          to="/achievements"
          className={cn(
            "relative flex flex-col rounded-xl border-2 border-foreground/20 bg-muted/80 dark:bg-muted/40 backdrop-blur-sm shadow-xl transition-all duration-300 min-w-[280px] overflow-hidden hover:border-foreground/40 hover:bg-muted dark:hover:bg-muted/60 cursor-pointer block",
            isVisible && !isRemoving
              ? "translate-x-0 opacity-100 scale-100"
              : "translate-x-full opacity-0 scale-95",
            isRemoving && "translate-x-full opacity-0 scale-95"
          )}
          role="alert"
        >
          <div className="flex items-center gap-2 px-4 pt-4 pb-2">
            <div className="shrink-0 h-10 w-10 rounded-xl bg-foreground/10 flex items-center justify-center">
              {a.imageUrl ? (
                <img src={a.imageUrl} alt="" className="h-8 w-8 object-contain rounded" />
              ) : (
                <Award className="h-5 w-5 text-foreground" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Achievement Unlocked</p>
              <p className="font-bold text-base truncate">{a.title}</p>
            </div>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClose();
              }}
              className="shrink-0 rounded-md p-1.5 hover:bg-foreground/10 transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {(a.description || a.xp != null) && (
            <div className="px-4 pb-4 pt-1 space-y-1">
              {a.description && <p className="text-sm text-muted-foreground line-clamp-2">{a.description}</p>}
              {a.xp != null && a.xp > 0 && (
                <p className="text-sm font-semibold text-foreground">+{a.xp} XP</p>
              )}
            </div>
          )}
        </Link>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          "relative flex items-start gap-3 rounded-lg border p-4 shadow-lg transition-all duration-300",
          styles[toast.type],
          isVisible && !isRemoving
            ? "translate-x-0 opacity-100"
            : "translate-x-full opacity-0",
          isRemoving && "translate-x-full opacity-0"
        )}
        role="alert"
      >
        <Icon className={cn("h-5 w-5 shrink-0 mt-0.5", iconColors[toast.type])} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">{toast.title}</div>
          {toast.message && (
            <div className="text-sm mt-1 opacity-90">{toast.message}</div>
          )}
        </div>
        <button
          onClick={handleClose}
          className="shrink-0 rounded-md p-1 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }
);
ToastComponent.displayName = "Toast";

export { ToastComponent };
